import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, api, login, type Account, type ConnectResult, type LoginResponse, type WorkersView } from './api'
import { AccountCard } from './components/AccountCard'
import { ConnectCard } from './components/ConnectCard'
import { InfoSheet } from './components/InfoSheet'
import { SubscriptionCard } from './components/SubscriptionCard'
import { WorkersList } from './components/WorkersList'
import { applyTheme } from './telegram'

type Phase = { kind: 'loading' } | { kind: 'ready' } | { kind: 'error'; message: string; hint?: string }

export function App() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const [me, setMe] = useState<LoginResponse['user'] | null>(null)
  const [account, setAccount] = useState<Account | null>(null)
  // What the server said about the game pass right after connecting, until the next action.
  const [connected, setConnected] = useState<ConnectResult | null>(null)
  const [sheet, setSheet] = useState<'terms' | 'support' | null>(null)
  const [workers, setWorkers] = useState<WorkersView | null>(null)
  // Two very different 503s. "No database" is a configuration state: the list is gone until the
  // operator fixes it. A gateway answering for a restarting API is a moment: the last good list
  // stays on screen and the next tick refreshes it.
  const [workersUnavailable, setWorkersUnavailable] = useState(false)
  const [workersStale, setWorkersStale] = useState(false)

  // Refreshes overlap: the 60 s timer, a toggle, a slow network. Only the newest response may win,
  // or a stale one would put a worker back to the state the user just changed.
  const loadSeq = useRef(0)

  const loadData = useCallback(async (): Promise<Account | null> => {
    const seq = ++loadSeq.current
    const fresh = await api.getAccount()
    if (seq !== loadSeq.current) return null
    setAccount(fresh)

    // A server without a database answers 503 here. That is a configuration state, not a failure of
    // the whole panel, so the account card still renders.
    try {
      const view = await api.getWorkers()
      if (seq !== loadSeq.current) return fresh
      setWorkers(view)
      setWorkersUnavailable(false)
      setWorkersStale(false)
    } catch (e) {
      if (seq !== loadSeq.current) return fresh
      if (e instanceof ApiError && e.isUnavailable) {
        setWorkers(null)
        setWorkersUnavailable(true)
        setWorkersStale(false)
      } else if (e instanceof ApiError && e.isServerRestarting) {
        // A restart may well be the operator attaching the database, so the "no database" verdict
        // is void until the API answers for itself again; two banners at once would contradict.
        setWorkersStale(true)
        setWorkersUnavailable(false)
      } else {
        throw e
      }
    }
    return fresh
  }, [])

  // A refresh that fails only because the panel token died interrupts; anything else waits for the next.
  const refresh = useCallback(
    () =>
      loadData().catch((e) => {
        if (e instanceof ApiError && e.isSessionExpired) showSessionExpiredRef.current()
        return null
      }),
    [loadData],
  )
  const showSessionExpiredRef = useRef<() => void>(() => {})

  const showSessionExpired = useCallback(() => {
    setPhase({
      kind: 'error',
      message: 'Сессия панели устарела',
      hint: 'Закройте и откройте панель заново — Telegram выдаст новый ключ входа.',
    })
  }, [])
  showSessionExpiredRef.current = showSessionExpired

  useEffect(() => {
    applyTheme()
    window.Telegram?.WebApp?.ready()
    window.Telegram?.WebApp?.expand()

    let cancelled = false
    ;(async () => {
      try {
        const session = await login()
        if (cancelled) return
        setMe(session.user)
        await loadData()
        if (!cancelled) setPhase({ kind: 'ready' })
      } catch (e) {
        if (cancelled) return
        if (e instanceof ApiError && e.code === 'no_init_data') {
          setPhase({
            kind: 'error',
            message: 'Панель открыта не из Telegram',
            hint: 'Откройте её через бота — авторизация работает только внутри Mini App.',
          })
        } else if (e instanceof ApiError && e.isOffline) {
          setPhase({
            kind: 'error',
            message: 'Нет связи с сервером',
            hint: 'Проверьте соединение и откройте панель заново.',
          })
        } else if (e instanceof ApiError && e.isSessionExpired) {
          showSessionExpired()
        } else if (e instanceof ApiError && e.isServerRestarting) {
          setPhase({
            kind: 'error',
            message: 'Сервер перезапускается',
            hint: 'Попробуйте через минуту — откройте панель заново.',
          })
        } else {
          setPhase({
            kind: 'error',
            message: 'Не удалось войти',
            hint: e instanceof Error ? e.message : undefined,
          })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [loadData, showSessionExpired])

  // Keep the countdowns honest while the panel sits open.
  useEffect(() => {
    if (phase.kind !== 'ready') return
    const timer = window.setInterval(() => {
      loadData().catch((e) => {
        // The one refresh failure worth interrupting for: the panel token died and the initData is
        // too old to mint another. Everything else is transient and the next tick retries.
        if (e instanceof ApiError && e.isSessionExpired) showSessionExpired()
      })
    }, 60_000)
    return () => window.clearInterval(timer)
  }, [phase.kind, loadData, showSessionExpired])

  if (phase.kind === 'loading') {
    return (
      <main className="centre">
        <div className="spinner" />
      </main>
    )
  }

  if (phase.kind === 'error') {
    return (
      <main className="centre">
        <h1>{phase.message}</h1>
        {phase.hint && <p className="muted">{phase.hint}</p>}
      </main>
    )
  }

  return (
    <main>
      <header>
        <h1>MadHouse</h1>
        {me && <p className="muted">{me.firstName ?? me.username ?? `#${me.id}`}</p>}
      </header>

      {account && !isConnected(account) && (
        <ConnectCard
          available={account.connectAvailable !== false}
          onConnected={async (result) => {
            setConnected(result)
            await refresh()
          }}
        />
      )}
      {account && isConnected(account) && <AccountCard account={account} onChanged={refresh} />}
      {connected?.message && (
        <p className={`banner ${connected.game === 'failed' ? 'bad' : ''}`} onClick={() => setConnected(null)}>
          {connected.message}
        </p>
      )}

      {account && <SubscriptionCard license={account.license} onChanged={refresh} onShowTerms={() => setSheet('terms')} />}

      {workersUnavailable && (
        <p className="banner">Сервер запущен без базы данных — воркеры недоступны.</p>
      )}
      {workersStale && <p className="banner">Сервер перезапускается, попробуйте через минуту.</p>}

      {workers && account && (
        <WorkersList
          workers={workers.workers}
          licenseActive={account.license.isActive}
          accountEnabled={workers.accountEnabled}
          onChanged={() =>
            loadData().then(
              () => undefined,
              (e) => {
                if (e instanceof ApiError && e.isSessionExpired) showSessionExpired()
              },
            )
          }
        />
      )}

      <footer className="footer">
        <button className="link small" onClick={() => setSheet('terms')}>
          Условия
        </button>
        <button className="link small" onClick={() => setSheet('support')}>
          Поддержка
        </button>
      </footer>

      {sheet && <InfoSheet topic={sheet} onClose={() => setSheet(null)} />}
    </main>
  )
}

/**
 * Connected: the Telegram session is stored. A server that predates the flag is judged by the session
 * state, as before.
 */
function isConnected(account: Account): boolean {
  return account.connected ?? !account.gameSession.needsUserAction
}
